import { BaseElement } from 'base';
import Swiper from 'swiper-core';

const REDUCE = '(prefers-reduced-motion: reduce)';
const SLIDE_SPEED = 300;
const EDITOR_BLOCK_ATTR = 'data-shopify-editor-block';
const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';

class InfographicBlock extends BaseElement {
  #swiper = null;
  #viewport = null;
  #track = null;
  #cards = [];
  #clones = [];
  #staticMq = null;
  #reduceMq = null;

  onConnect() {
    const configError = this.dataset.configError;
    if (configError) {
      console.error(`[infographic-block] ${configError}`);
      return;
    }

    this.#viewport = this.q('[data-carousel]');
    this.#track = this.q('[data-carousel-track]');
    this.#cards = this.qa('[data-carousel-card]');
    if (!this.#viewport || !this.#track || this.#cards.length < 2) return;

    this.#staticMq = window.matchMedia(this.dataset.staticMedia);
    this.#reduceMq = window.matchMedia(REDUCE);

    this.listen(this.#staticMq, 'change', this.#sync);
    this.listen(this.#reduceMq, 'change', this.#onReduceChange);
    this.listen(window, 'resize', this.#onWindowResize);
    this.listen(this.#viewport, 'keydown', this.#onKeydown);
    this.cleanup(() => this.#unmount());

    this.#sync();
  }

  #sync() {
    if (this.#staticMq.matches) this.#unmount();
    else this.#mount();
  }

  #mount() {
    if (this.#swiper) return;

    const viewport = this.#viewport;
    viewport.classList.add('swiper');
    this.#track.classList.add('swiper-wrapper');
    for (const card of this.#cards) card.classList.add('swiper-slide');
    viewport.setAttribute('role', 'group');
    viewport.tabIndex = 0;

    this.#addClones();
    this.#initSwiper();
  }

  #unmount() {
    if (!this.#swiper) return;

    this.#swiper.destroy(true, true);
    this.#swiper = null;
    this.#removeClones();

    const viewport = this.#viewport;
    viewport.classList.remove('swiper', 'swiper-backface-hidden');
    this.#track.classList.remove('swiper-wrapper');
    for (const card of this.#cards) {
      card.classList.remove('swiper-slide');
      card.removeAttribute('inert');
    }
    viewport.removeAttribute('role');
    viewport.removeAttribute('tabindex');
  }

  #initSwiper() {
    this.#swiper = new Swiper(this.#viewport, {
      slidesPerView: 'auto',
      spaceBetween: this.#readLength('--infographic-block-gap'),
      loop: true,
      watchSlidesProgress: true,
      speed: this.#slideSpeed(),
      on: {
        afterInit: instance => this.#syncInert(instance),
        progress: instance => this.#syncInert(instance),
        resize: instance => this.#onResize(instance)
      }
    });
  }

  #requiredSlides() {
    const [card] = this.#cards;
    const slot = card.offsetWidth + this.#readLength('--infographic-block-gap');
    if (!slot) return this.#cards.length * 2;
    const width =
      this.#viewport.clientWidth + this.#readLength('--grid-margin');
    return Math.ceil(width / slot) * 2 + 2;
  }

  #addClones() {
    const required = this.#requiredSlides();
    let total = this.#cards.length;
    while (total < required) {
      for (const card of this.#cards) {
        const clone = card.cloneNode(true);
        clone.removeAttribute('data-carousel-card');
        clone.removeAttribute(EDITOR_BLOCK_ATTR);
        clone.setAttribute('aria-hidden', 'true');
        this.#track.append(clone);
        this.#clones.push(clone);
      }
      total += this.#cards.length;
    }
    const cloned = `[data-carousel-track] > :not([data-carousel-card]) :is(${FOCUSABLE})`;
    for (const focusable of this.qa(cloned)) focusable.tabIndex = -1;
  }

  #removeClones() {
    for (const clone of this.#clones) clone.remove();
    this.#clones = [];
  }

  #onWindowResize() {
    if (this.#staticMq.matches === Boolean(this.#swiper)) {
      this.#sync();
      return;
    }
    const instance = this.#swiper;
    if (instance && !instance.destroyed) this.#onResize(instance);
  }

  #onResize(instance) {
    if (instance.slides.length < this.#requiredSlides()) {
      queueMicrotask(() => {
        if (!this.isConnected || instance !== this.#swiper) return;
        this.#unmount();
        this.#mount();
      });
      return;
    }
    const gap = this.#readLength('--infographic-block-gap');
    if (instance.params.spaceBetween !== gap) {
      instance.params.spaceBetween = gap;
      instance.update();
    }
    this.#syncInert(instance);
  }

  #syncInert(instance) {
    const visibleClass = instance.params.slideVisibleClass;
    for (const slide of instance.slides) {
      slide.toggleAttribute('inert', !slide.classList.contains(visibleClass));
    }
  }

  #slideSpeed() {
    return this.#reduceMq?.matches ? 0 : SLIDE_SPEED;
  }

  #onReduceChange() {
    if (this.#swiper) this.#swiper.params.speed = this.#slideSpeed();
  }

  #readLength(name) {
    const raw = getComputedStyle(this.#viewport).getPropertyValue(name);
    return parseFloat(raw) || 0;
  }

  #onKeydown(event) {
    if (event.target !== this.#viewport) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (!this.#swiper || this.#swiper.isLocked) return;
    event.preventDefault();
    const forward = this.#swiper.rtlTranslate
      ? event.key === 'ArrowLeft'
      : event.key === 'ArrowRight';
    if (forward) this.#swiper.slideNext();
    else this.#swiper.slidePrev();
  }
}

if (!customElements.get('infographic-block')) {
  customElements.define('infographic-block', InfographicBlock);
}
